import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import LoginStyles from "./Login.module.css";
import { useGoogleLogin } from '@react-oauth/google';

import { useDispatch } from 'react-redux';
import { signinGoogle, signin, resendVerification } from "../../redux/actions/auth";
import { toast } from 'react-toastify';

function Login({ isModal = false, onLoginSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [expiredEmail, setExpiredEmail] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const handleLoginSuccess = (userData) => {
    if (isModal) {
      if (onLoginSuccess) onLoginSuccess(userData);
      return;
    }

    const params = new URLSearchParams(location.search);
    const redirectParam = params.get('redirect');

    if (redirectParam) {
      const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      let syncParam = '';
      if (userData) {
        try {
          const userPayload = JSON.stringify(userData);
          syncParam = `_auth_sync=${encodeURIComponent(btoa(unescape(encodeURIComponent(userPayload))))}`;
        } catch (e) {
          console.error('Failed to encode dev auth sync', e);
        }
      }

      if (redirectParam.startsWith('http://localhost:3000')) {
        const sep = redirectParam.includes('?') ? '&' : '?';
        window.location.replace(syncParam ? `${redirectParam}${sep}${syncParam}` : redirectParam);
        return;
      }

      if (isLocalDev && (
        redirectParam.startsWith('/pricing') || 
        redirectParam.startsWith('/courses') || 
        redirectParam.startsWith('/about') || 
        redirectParam.startsWith('/home') || 
        redirectParam.startsWith('/canada-pr') || 
        redirectParam.startsWith('/celpip-') || 
        redirectParam.startsWith('/pte-')
      )) {
        const sep = redirectParam.includes('?') ? '&' : '?';
        window.location.replace(`http://localhost:3000${redirectParam}${syncParam ? `${sep}${syncParam}` : ''}`);
        return;
      }

      if (redirectParam.startsWith('/')) {
        window.location.replace(redirectParam);
        return;
      }

      window.location.replace(redirectParam);
      return;
    }

    navigate("/dashboard");
  };

  function handleGoogleLoginSuccess(tokenResponse) {
    const googleAccessToken = tokenResponse.access_token || tokenResponse.credential;

    // Send to backend
    dispatch(signinGoogle(
      { googleAccessToken }, 
      null, 
      handleLoginSuccess
    ));
  }

  function handleGoogleLoginError(error) {
    console.error("Google login error:", error);
    toast.error("Google login failed");
  }

  const login = useGoogleLogin({
    onSuccess: handleGoogleLoginSuccess,
    onError: handleGoogleLoginError
  });

  function handleSubmit(e) {
    e.preventDefault();
    if (email === "" || password === "") {
      toast.error("Please fill in all fields.");
      return;
    }
    dispatch(signin(
      { email, password }, 
      null, 
      handleLoginSuccess
    ))
      .catch(err => {
        if (err.isExpired) {
          setExpiredEmail(err.email);
        }
      });
  }

  function handleResend() {
    if (expiredEmail) {
      dispatch(resendVerification(expiredEmail));
      setExpiredEmail(null); // Reset after sending
    }
  }

  return (
    <div className={isModal ? LoginStyles.loginContainerModal : LoginStyles.loginContainer}>
      <div className={LoginStyles.loginBox}>
        {/* Left Side: Animated Headlines */}
        <div className={LoginStyles.loginLeftPanel}>
          <div className={LoginStyles.animatedTextWrapper}>
            <p className={LoginStyles.slideUp3} style={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
              START YOUR JOURNEY FOR 10+ SCORES
            </p>
            <h2 className={LoginStyles.slideUp1} style={{ fontSize: '28px', lineHeight: '1.3' }}>
              Strategy videos by Namrata Palta, author of English language books
            </h2>
            <div className={LoginStyles.slideUp2} style={{ fontSize: '16px', color: '#f0d9e0', marginTop: '15px' }}>
              <ul style={{ listStyleType: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <li><i className="fa-solid fa-circle-check" style={{ marginRight: '10px', color: '#ffb3c6' }}></i>Best Practice Tests</li>
                <li><i className="fa-solid fa-circle-check" style={{ marginRight: '10px', color: '#ffb3c6' }}></i>Easy Strategies & Detailed Courses</li>
                <li><i className="fa-solid fa-circle-check" style={{ marginRight: '10px', color: '#ffb3c6' }}></i>Easy Templates & Vocabulary Builders</li>
              </ul>
            </div>
            <div className={LoginStyles.slideUp3} style={{ marginTop: '25px', textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
              <img 
                src="/girl_thumbs_up.png" 
                alt="Thumbs Up" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '240px', 
                  objectFit: 'contain',
                  mixBlendMode: 'lighten', /* Blends dark background into the maroon gradient */
                  WebkitMaskImage: 'radial-gradient(circle at center, black 45%, transparent 75%)',
                  maskImage: 'radial-gradient(circle at center, black 45%, transparent 75%)'
                }} 
              />
            </div>
          </div>
        </div>

        {/* Right Side: Form */}
        <div className={LoginStyles.loginContainerv2}>
          <div className={LoginStyles.header}>
            <h1>Welcome Back!</h1>
            <p>Sign in</p>
          </div>

        {expiredEmail && (
          <div style={{ textAlign: 'center', marginBottom: '15px', padding: '10px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '5px', border: '1px solid #ffeeba' }}>
            <p style={{ margin: 0, marginBottom: '10px' }}>Verification code expired.</p>
            <button
              onClick={handleResend}
              style={{
                padding: '5px 10px',
                backgroundColor: '#856404',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                cursor: 'pointer'
              }}
            >
              Resend Verification
            </button>
          </div>
        )}

        <div className={LoginStyles.inputContainer}>
          <label>YOUR EMAIL</label>
          <input
            onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com"
            type="email"
          />
        </div>

        <div className={LoginStyles.inputContainer}>
          <label>PASSWORD</label>
          <div style={{ position: 'relative' }}>
            <input
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter your password"
              type="password"
            />
            <i className="fa-regular fa-eye" style={{ position: 'absolute', right: '15px', top: '15px', color: '#aaa', cursor: 'pointer' }}></i>
          </div>
        </div>

        <div className={LoginStyles.forgetmeContainer}>
          <div className={LoginStyles.rememberMe}>
            <input type="checkbox" id="rememberMe" />
            <label htmlFor="rememberMe">Remember Me</label>
          </div>
          <div>
            <Link to="/forgotpassword">Forgot Password?</Link>
          </div>
        </div>

        <button onClick={handleSubmit} className={LoginStyles.loginBTN}>
          Continue with email
        </button>

        <button onClick={() => login()} className={LoginStyles.googleBTN}>
          <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="Google" style={{ width: '18px', marginRight: '10px' }} />
          Continue with Google
        </button>

        <span className={LoginStyles.notreg}>
          Don't have an account? <Link className={LoginStyles.singupBTN} to={`/signup${location.search}`} state={{ from: isModal ? location.pathname : (new URLSearchParams(location.search).get('redirect') || undefined) }}>Register</Link>
        </span>
      </div>
      </div>
    </div>
  );
}

export default Login;
